import React, { useMemo } from 'react';
import colors from 'assets/styles/colors';
import { Anonymous } from 'assets/svgs';
import clsx from 'clsx';
import CryptoIcon from 'components/CurrencyIcons/CryptoIcon';
import FormattedAmount from 'components/FormattedAmount';
import ModalLink from 'components/Link/ModalLink';
import VipBadge from 'components/Vip/VipBadge';
import WrappedImage from 'components/WrappedImage';
import { Challenge, VipLevel } from 'generated/graphql';
import { useUsdToPreferredFiat } from 'hooks/useFiatRate';
import { ModalUrl } from 'hooks/useModal';
import usePreference from 'hooks/usePreference';
import { vipLevelMap } from 'hooks/vip/useVipLevel';
import { ImageLoaderProps } from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { imageQuality } from 'utils/imageQuality';
import { gameLink, getImageFromKey } from 'utils/linkGenerator';

import { Tooltip } from '../../Tooltip';
import { GameCardBorder } from '../styles';
import { TallGameCardElement } from '../TallGameCard/styles';

import styles from './ChallengeCard.module.scss';

interface ChallengeCardProps {
  challenge: Challenge;
  initLoading?: boolean;
  isHideFooter?: boolean;
}

const quality = imageQuality();

export const ChallengeCard = React.memo(
  ({ challenge, initLoading, isHideFooter = false }: ChallengeCardProps) => {
    const { currency, referredOnly, game, challengeReward, creator } = challenge;
    const { fiatPreference } = usePreference();

    const creatorName = creator?.username || 'Shuffle';
    const creatorVipLevel = creator?.vipLevel || null;

    const { t } = useTranslation();
    const { displayInFiat } = usePreference();
    const image = useMemo(() => getImageFromKey(game?.image?.key, !game?.name), [game]);

    const loader = ({ src, width }: ImageLoaderProps) => {
      return `${src}&width=${width}&q=${quality}`;
    };

    const minBetAmountFiat = useUsdToPreferredFiat(challenge.usdMinBet);

    const minMultiplierFormatted = t('intlNumber', {
      val: challenge.minMultiplier,
    });

    const minBetAmount = t('intlCurrencyWithOptions', {
      val: minBetAmountFiat,
      currency: fiatPreference,
    });

    const challengeName = t('txtChallengeNameV2', {
      minMultiplier: minMultiplierFormatted,
      minBet: minBetAmount,
    });

    return (
      <div className={styles.challengeCardWrapper}>
        <Link href={gameLink(game?.slug ?? '')}>
          <a>
            <TallGameCardElement>
              {referredOnly && <div className={styles.exclusiveTag}>{t('txtExclusive')}</div>}
              <WrappedImage
                src={`${image}?auto=format`}
                loader={loader}
                alt="tall-game-card"
                layout="fill"
                objectFit="contain"
                placeholder="blur"
                sizes="180px"
                blurDataURL={`${image}?auto=compress&blur=200&width=20`}
              />
              <GameCardBorder $accent={game?.accentColor} />
            </TallGameCardElement>
            {!initLoading && (
              <div
                className={clsx({
                  [styles.challengeFooter]: true,
                  [styles.challengeFooterHide]: isHideFooter,
                })}
              >
                <div className={styles.challengeName}>{challengeName}</div>
                <div>
                  <div className={styles.challengeLabel}>{t('txtReward')}</div>
                  <div className={styles.challengeInfoBlock}>
                    {displayInFiat && <CryptoIcon currency={currency} />}
                    <FormattedAmount
                      showIcon={!displayInFiat}
                      value={challenge.amount}
                      currency={currency}
                    />
                  </div>
                </div>

                <div>
                  <div className={styles.challengeLabel}>{t('txtCreator')}</div>
                  <ModalLink modal={ModalUrl.USER} params={{ name: creatorName }}>
                    <div className={styles.challengeInfoBlock}>
                      {creatorVipLevel && (
                        <VipBadge {...vipLevelMap[creatorVipLevel as VipLevel]} />
                      )}
                      <div
                        className={`${styles.challengeName} ${styles.challengeCreator} ${styles.textTruncated}`}
                      >
                        {creatorName}
                      </div>
                    </div>
                  </ModalLink>
                </div>

                {challengeReward?.user && (
                  <div className={styles.challengeCompletedWrapper}>
                    <div className={styles.challengeLabel}>{t('txtCompletedBy')}</div>
                    {challengeReward?.user?.username ? (
                      <ModalLink
                        modal={ModalUrl.USER}
                        params={{ name: challengeReward?.user?.username }}
                      >
                        <div
                          className={`${styles.challengeInfoBlock} ${styles.challengeInfoBlockCenter}`}
                        >
                          {challengeReward?.user?.vipLevel && (
                            <VipBadge
                              {...vipLevelMap[challengeReward?.user?.vipLevel as VipLevel]}
                            />
                          )}
                          <div
                            className={`${styles.challengeName} ${styles.challengeCreator} ${styles.textTruncated}`}
                          >
                            {challengeReward?.user?.username}
                          </div>
                        </div>
                      </ModalLink>
                    ) : (
                      <Tooltip placement="top" content={t('tooltipUserPrivacyEnabled')}>
                        <div
                          className={`${styles.challengeInfoBlock} ${styles.challengeInfoBlockCenter}`}
                        >
                          <Anonymous color={colors.gray400} />
                          <div
                            className={`${styles.challengeName} ${styles.challengeCreator} ${styles.challengeNameHidden}`}
                          >
                            {t('txtHidden')}
                          </div>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            )}
          </a>
        </Link>
      </div>
    );
  },
);
